import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Checkbox,
  Button,
  Box,
  Grid,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import { EstimateResponse, EstimateSendRequest, EstimateConversionRequest } from '../../types/estimate';
import { EstimateDialogState } from '../../hooks/useEstimateActions';

interface EstimateDialogsProps {
  estimate: EstimateResponse;
  dialogs: EstimateDialogState;
  actionLoading: boolean;
  defaultSendData: EstimateSendRequest;
  defaultConvertData: EstimateConversionRequest;
  onCloseDialog: (dialogType: keyof EstimateDialogState) => void;
  onSendEstimate: (data: EstimateSendRequest) => Promise<{ success: boolean }>;
  onConvertToSalesOrder: (data: EstimateConversionRequest) => Promise<{ success: boolean }>;
  onDeleteEstimate: () => Promise<{ success: boolean }>;
}

const EstimateDialogs: React.FC<EstimateDialogsProps> = ({
  estimate,
  dialogs,
  actionLoading,
  defaultSendData,
  defaultConvertData,
  onCloseDialog,
  onSendEstimate,
  onConvertToSalesOrder,
  onDeleteEstimate
}) => {
  const [sendData, setSendData] = useState<EstimateSendRequest>(defaultSendData);
  const [convertData, setConvertData] = useState<EstimateConversionRequest>(defaultConvertData);
  const [warehouses, setWarehouses] = useState<Array<{ id: string; name: string }>>([]);

  useEffect(() => {
    if (!dialogs.convertDialogOpen) return;
    const token = localStorage.getItem('token');
    fetch('/api/admin/inventory/warehouses', {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then((res) => (res.ok ? res.json() : Promise.resolve([])))
      .then((data) => {
        setWarehouses(data);
        const savedWarehouseId = localStorage.getItem('admin_selectedWarehouseId');
        if (!convertData.warehouse_id) {
          const defaultId = savedWarehouseId && data.some((w: any) => w.id === savedWarehouseId)
            ? savedWarehouseId
            : (data[0]?.id || '');
          setConvertData((prev) => ({ ...prev, warehouse_id: defaultId }));
        }
      })
      .catch(() => setWarehouses([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dialogs.convertDialogOpen]);

  const handleSendSubmit = async () => {
    const result = await onSendEstimate(sendData);
    if (result.success) {
      setSendData(defaultSendData); // Reset form
    }
  };

  const handleConvertSubmit = async () => {
    const result = await onConvertToSalesOrder(convertData);
    if (result.success) {
      setConvertData(defaultConvertData); // Reset form
    }
  };

  const handleDeleteSubmit = async () => {
    await onDeleteEstimate();
  };

  return (
    <>
      {/* Send Dialog */}
      <Dialog
        open={dialogs.sendDialogOpen}
        onClose={() => onCloseDialog('sendDialogOpen')}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Send Estimate</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={sendData.send_via_email}
                  onChange={(e) => setSendData(prev => ({ ...prev, send_via_email: e.target.checked }))}
                />
              }
              label="Send via Email"
            />

            <FormControlLabel
              control={
                <Checkbox
                  checked={sendData.send_via_sms}
                  onChange={(e) => setSendData(prev => ({ ...prev, send_via_sms: e.target.checked }))}
                />
              }
              label="Send via SMS"
            />

            <FormControlLabel
              control={
                <Checkbox
                  checked={sendData.send_via_whatsapp}
                  onChange={(e) => setSendData(prev => ({ ...prev, send_via_whatsapp: e.target.checked }))}
                />
              }
              label="Send via WhatsApp"
            />

            <TextField
              fullWidth
              label="Custom Message (Optional)"
              multiline
              rows={3}
              value={sendData.custom_message}
              onChange={(e) => setSendData(prev => ({ ...prev, custom_message: e.target.value }))}
              sx={{ mt: 2 }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => onCloseDialog('sendDialogOpen')}>Cancel</Button>
          <Button
            onClick={handleSendSubmit}
            variant="contained"
            color="secondary"
            disabled={actionLoading || (!sendData.send_via_email && !sendData.send_via_sms && !sendData.send_via_whatsapp)}
          >
            {actionLoading ? 'Sending...' : 'Send'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Convert Dialog */}
      <Dialog
        open={dialogs.convertDialogOpen}
        onClose={() => onCloseDialog('convertDialogOpen')}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Convert to Sales Order</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Order Date"
                  type="date"
                  value={convertData.order_date}
                  onChange={(e) => setConvertData(prev => ({ ...prev, order_date: e.target.value }))}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>

              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Expected Delivery Date"
                  type="date"
                  value={convertData.expected_delivery_date}
                  onChange={(e) => setConvertData(prev => ({ ...prev, expected_delivery_date: e.target.value }))}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>

              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Fulfillment Warehouse</InputLabel>
                  <Select
                    label="Fulfillment Warehouse"
                    value={convertData.warehouse_id || ''}
                    onChange={(e) => setConvertData(prev => ({ ...prev, warehouse_id: e.target.value }))}
                  >
                    {warehouses.map((wh) => (
                      <MenuItem key={wh.id} value={wh.id}>{wh.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Shipping Charges"
                  type="number"
                  value={convertData.shipping_charges}
                  onChange={(e) => setConvertData(prev => ({ ...prev, shipping_charges: parseFloat(e.target.value) || 0 }))}
                  inputProps={{ min: 0, step: 0.01 }}
                />
              </Grid>

              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Packing Charges"
                  type="number"
                  value={convertData.packing_charges}
                  onChange={(e) => setConvertData(prev => ({ ...prev, packing_charges: parseFloat(e.target.value) || 0 }))}
                  inputProps={{ min: 0, step: 0.01 }}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Notes"
                  multiline
                  rows={3}
                  value={convertData.notes}
                  onChange={(e) => setConvertData(prev => ({ ...prev, notes: e.target.value }))}
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => onCloseDialog('convertDialogOpen')}>Cancel</Button>
          <Button
            onClick={handleConvertSubmit}
            variant="contained"
            color="secondary"
            disabled={actionLoading}
          >
            {actionLoading ? 'Converting...' : 'Convert'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={dialogs.deleteDialogOpen} onClose={() => onCloseDialog('deleteDialogOpen')}>
        <DialogTitle>Delete Estimate</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete estimate {estimate.estimate_number}? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => onCloseDialog('deleteDialogOpen')}>Cancel</Button>
          <Button
            onClick={handleDeleteSubmit}
            color="error"
            variant="contained"
            disabled={actionLoading}
          >
            {actionLoading ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default EstimateDialogs;